// @ts-strict-ignore

import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';

import { AnalyticEventAction } from 'analytics';

import { Dialogs } from 'analytics/events/dialog';
import { trackEditTicketModalAnalyticsEvent } from 'analytics/events/edit-ticket-modal';

import { useStores } from 'mobx/hooks/useStores';

import Patient from 'models/Patient';
import { ProtocolName, ProtocolType } from 'models/ScheduledProtocol';
import Ticket from 'models/Ticket';

import { ResolveTicketsData } from 'hooks/useTicketResolve';

import { useToggle } from 'hooks/useToggle';

import EditTaskModal from 'views/Modals/EditTaskModal';
import EditTicketModal from 'views/Modals/EditTicketModal';
import { MergeLocalPatientsModal } from 'views/Modals/MergePatientsModal/MergeLocalPatientsModal';
import { DuplicatePatientData } from 'views/Modals/MergePatientsModal/MergePatientsModal.types';
import SendSmsPersonalRequest from 'views/Modals/SendSmsPersonalRequest';

import { HidePatientDialog } from 'components/MessageDialogs/HidePatientDialog';

import { ResolveOrCloseTicketWithMessageForTheFirstTimeDialog } from 'components/MessageDialogs/ResolveOrCloseTicketWithMessageForTheFirstTimeDialog';
import { UndeliveredMessageToPatientDialog } from 'components/MessageDialogs/UndeliveredMessageToPatientDialog';
import { ResolveAndCommentModal } from 'components/Ticket/ResolveAndCommentModal';
import useTicketOverviewContext from 'components/Ticket/TicketsContainers/useTicketOverviewContext';
import { MessageDialog } from 'components/UIkit/atoms/Dialog';

type UseEditModalReturns = [ReactElement, (patient: Patient, ticket?: Ticket) => void];

export function useEditTaskModal({
  onEditTask,
  onDeleteTask
}: {
  onEditTask: (...args: any) => any;
  onDeleteTask: (...args: any) => any;
}): UseEditModalReturns {
  const [isEditTaskModalOpened, setIsEditTaskModalOpened] = useState(false);
  const [patient, setPatient] = useState<Patient>();
  const [ticket, setTicket] = useState<Ticket>();

  const openEditTaskTicketModal = (patient: Patient, ticket?: Ticket) => {
    setIsEditTaskModalOpened(true);
    setPatient(patient);
    if (ticket) {
      setTicket(ticket);
    }
  };

  const closeEditTaskTicketModal = () => {
    setIsEditTaskModalOpened(false);
  };

  const modal = (
    <EditTaskModal
      onDeleteTask={onDeleteTask}
      onEditTask={onEditTask}
      isOpen={isEditTaskModalOpened}
      onCancel={closeEditTaskTicketModal}
      onSubmit={closeEditTaskTicketModal}
      resetDataAfterClose={() => {
        setPatient(null);
        setTicket(null);
      }}
      patient={patient}
      ticket={ticket}
    />
  );
  return [modal, openEditTaskTicketModal];
}

export function useEditModal({
  onEditTicket,
  onDeleteTicket
}: {
  onEditTicket: (...args: any) => any;
  onDeleteTicket: (...args: any) => any;
}): UseEditModalReturns {
  const [isEditModalOpened, setIsEditModalOpened] = useState(false);
  const [patient, setPatient] = useState<Patient>();
  const [ticket, setTicket] = useState<Ticket>();

  const openEditTicketModal = (patient: Patient, ticket: Ticket) => {
    trackEditTicketModalAnalyticsEvent({ action: AnalyticEventAction.Open, ticket_id: ticket.id });
    setIsEditModalOpened(true);
    setPatient(patient);
    setTicket(ticket);
  };

  const closeEditTicketModal = () => {
    setIsEditModalOpened(false);
  };

  const modal = useMemo(
    () => (
      <EditTicketModal
        onDeleteTicket={onDeleteTicket}
        onEditTicket={onEditTicket}
        isOpen={isEditModalOpened}
        onCancel={closeEditTicketModal}
        onSubmit={closeEditTicketModal}
        resetDataAfterClose={() => {
          setTicket(null);
          setPatient(null);
        }}
        patient={patient}
        ticket={ticket}
      />
    ),
    [onDeleteTicket, onEditTicket, isEditModalOpened, patient, ticket]
  );

  return [modal, openEditTicketModal];
}

type UseSmsModalReturnType = [ReactElement, (patient: Patient) => void];
export const useSmsModal = (): UseSmsModalReturnType => {
  const { userStore, departmentsStore, patientPageStore } = useStores();
  const [patient, setPatient] = useState<Patient>();
  const [smsMessageText, setSmsMessageText] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const { actionCallbacks } = useTicketOverviewContext();
  const { onRequestReport } = actionCallbacks;

  const getRequestDetails = useCallback(
    () => ({
      patientId: patient?.id,
      message: smsMessageText,
      reportType: ProtocolType.mobile,
      reportName: ProtocolName.symptom
    }),
    [patient?.id, smsMessageText]
  );

  const handleSendRequest = useCallback(() => {
    async function makeSendReportRequest() {
      try {
        setIsSendingSms(true);
        await patientPageStore!.sendReportRequest(patient, getRequestDetails);
        onRequestReport && onRequestReport(patient);
      } finally {
        setIsSendingSms(false);
        setIsSmsModalOpen(false);
      }
    }

    makeSendReportRequest();
  }, [getRequestDetails, patient, patientPageStore, onRequestReport]);

  const toggleSmsModal = (patient: Patient) => {
    setPatient(patient);
    setSmsMessageText(
      patient.getDefaultReportReminderText(
        userStore.currentDoctor.firstName,
        departmentsStore!.root.name
      )
    );
    setIsSmsModalOpen((currentlyOpen) => !currentlyOpen);
  };

  const modal = useMemo(
    () => (
      <SendSmsPersonalRequest
        onSendClicked={handleSendRequest}
        onMessageChanged={setSmsMessageText}
        onCancelClicked={() => setIsSmsModalOpen(false)}
        isSending={isSendingSms}
        isOpen={isSmsModalOpen}
        message={smsMessageText}
        title={`SMS to ${patient?.fullName}`}
      />
    ),
    [handleSendRequest, isSendingSms, isSmsModalOpen, smsMessageText, patient]
  );

  return [modal, toggleSmsModal];
};

type UseTicketResolveAndCommentModalReturns = [
  ReactElement,
  (tickets: Ticket[], patientId?: number) => void
];
export function useTicketResolveAndCommentModal(): UseTicketResolveAndCommentModalReturns {
  const { isOpen, setIsOpen } = useToggle(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [patientId, setPatientId] = useState<number>();

  const openModal = (tickets: Ticket[], patientId?: number) => {
    setTickets(tickets);
    setPatientId(patientId);
    setIsOpen(true);
  };

  const closeModal = useCallback(() => {
    setTickets([]);
    setIsOpen(false);
  }, [setIsOpen]);

  const modal = useMemo(() => {
    let modalNode = null;
    if (tickets.length > 0) {
      modalNode = (
        <ResolveAndCommentModal
          isOpen={isOpen}
          onClose={closeModal}
          tickets={tickets}
          patientId={patientId}
        />
      );
    }
    return modalNode;
  }, [closeModal, isOpen, tickets, patientId]);

  return [modal, openModal];
}

export type DisconnectPopupConfig = {
  ticketIds: number[];
  callback: (...args: any) => any;
  excludedCallIds?: number[];
  action?: 'close' | 'resolve';
};

type UseDisconnectPopupReturn = [JSX.Element, (disconnectConfig: DisconnectPopupConfig) => any];
export const useDisconnectDraftPopup = (): UseDisconnectPopupReturn => {
  const { callsStore } = useStores();
  const [currentTicketIds, setTicketIds] = useState<number[]>(null);
  const [isDisconnectDraftPopupOpen, setIsDisconnectDraftPopupOpen] = useState(false);
  const [currentExcludedCallIds, setCurrentExcludedCallIds] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState<'close' | 'resolve'>('resolve');
  const callbackAfterDisconnect = useRef(null);

  const openDisconnectDraftPopup = ({
    ticketIds,
    callback,
    excludedCallIds,
    action = 'resolve'
  }: DisconnectPopupConfig) => {
    setTicketIds(ticketIds);
    setIsDisconnectDraftPopupOpen(true);
    setCurrentExcludedCallIds(excludedCallIds);
    setCurrentAction(action); // Action is determined by caller each time popup opens
    callbackAfterDisconnect.current = callback;
  };

  const disconnectDraft = useCallback(async () => {
    try {
      // when submitting a resumed draft we do not want to disconnect the current edited draft,
      // this is when excludedCallIds should be used, it only matters when
      // there is more than one ticket/draft when submitting a call
      setIsLoading(true);
      await callsStore.disconnectDraftsFromTickets(currentTicketIds, currentExcludedCallIds);
      setIsDisconnectDraftPopupOpen(false);
      callbackAfterDisconnect.current && callbackAfterDisconnect.current();
    } finally {
      resetPopup();
    }
  }, [callsStore, currentExcludedCallIds, currentTicketIds]);

  const resetPopup = () => {
    setIsLoading(false);
    setIsDisconnectDraftPopupOpen(false);
    setTicketIds(null);
    setCurrentExcludedCallIds(null);
    callbackAfterDisconnect.current = null;
  };

  const actionText = currentAction === 'close' ? 'Close' : 'Resolve';
  const actionTextLower = actionText.toLowerCase();

  const disconnectDraftPopup = useMemo(
    () =>
      currentTicketIds ? (
        <MessageDialog
          id={Dialogs.DisconnectDraftAndResolve}
          isOpen={isDisconnectDraftPopupOpen}
          title={`Disconnect Call Draft from Ticket & ${actionText}?`}
          secondaryActionProps={{ text: 'Cancel', onClick: resetPopup }}
          handleClose={resetPopup}
          primaryActionProps={{
            text: isLoading ? 'Disconnecting...' : `Disconnect & ${actionText}`,
            onClick: () => {
              !isLoading && disconnectDraft();
            }
          }}
        >
          The ticket(s) you are attempting to {actionTextLower} is connected to at least one Call
          Draft. Press “Disconnect & {actionText}” to disconnect the Call Draft and proceed. The
          Call Draft will not be erased, and can be found in the Patient Page. To {actionTextLower}{' '}
          the ticket(s) with this Call Draft, the user who created it must first submit the call.
        </MessageDialog>
      ) : null,
    [
      currentTicketIds,
      isDisconnectDraftPopupOpen,
      isLoading,
      disconnectDraft,
      actionText,
      actionTextLower
    ]
  );

  return [disconnectDraftPopup, openDisconnectDraftPopup];
};

interface UseResolveTicketDialog {
  dialog: JSX.Element;
  openDialog: (resolveTicketsData: ResolveTicketsData, action?: 'close' | 'resolve') => void;
}

export const useResolveOrCloseTicketWithMessageForTheFirstTimeDialog =
  (): UseResolveTicketDialog => {
    const [isOpen, setIsOpen] = useState(false);
    const [resolveTicketsData, setResolveTicketsData] = useState<ResolveTicketsData>({
      tickets: [],
      comment: ''
    });
    const [action, setAction] = useState<'close' | 'resolve'>('resolve');

    const openDialog = (
      resolveTicketsData: ResolveTicketsData,
      actionParam: 'close' | 'resolve' = 'resolve'
    ) => {
      setResolveTicketsData(resolveTicketsData);
      setAction(actionParam);
      setIsOpen(true);
    };

    const dialog = useMemo(
      () => (
        <ResolveOrCloseTicketWithMessageForTheFirstTimeDialog
          isOpen={isOpen}
          closeDialog={() => setIsOpen(false)}
          resolveTicketsData={resolveTicketsData}
          action={action}
        />
      ),

      [isOpen, setIsOpen, resolveTicketsData, action]
    );

    return { dialog, openDialog };
  };

export const useUndeliveredMessageToPatientDialog = (): UseResolveTicketDialog => {
  const [isOpen, setIsOpen] = useState(false);
  const [resolveTicketsData, setResolveTicketsData] = useState<ResolveTicketsData>({
    tickets: [],
    comment: ''
  });
  const [action, setAction] = useState<'close' | 'resolve'>('resolve');

  const openDialog = (
    resolveTicketsData: ResolveTicketsData,
    actionParam: 'close' | 'resolve' = 'resolve'
  ) => {
    setResolveTicketsData(resolveTicketsData);
    setAction(actionParam);
    setIsOpen(true);
  };

  const dialog = useMemo(
    () => (
      <UndeliveredMessageToPatientDialog
        isOpen={isOpen}
        closeDialog={() => setIsOpen(false)}
        resolveTicketsData={resolveTicketsData}
        action={action}
      />
    ),

    [isOpen, setIsOpen, resolveTicketsData, action]
  );

  return { dialog, openDialog };
};

export const useHidePatientDialog = (onClose?: () => void): UseResolveTicketDialog => {
  const [isOpen, setIsOpen] = useState(false);
  const [resolveTicketsData, setResolveTicketsData] = useState<ResolveTicketsData>({
    tickets: [],
    comment: ''
  });
  const [action, setAction] = useState<'close' | 'resolve'>('resolve');

  const openDialog = (
    resolveTicketsData: ResolveTicketsData,
    actionParam: 'close' | 'resolve' = 'resolve'
  ) => {
    setResolveTicketsData(resolveTicketsData);
    setAction(actionParam);
    setIsOpen(true);
  };

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    onClose && onClose();
  }, [onClose]);

  const dialog = useMemo(
    () => (
      <HidePatientDialog
        isOpen={isOpen}
        closeDialog={closeDialog}
        resolveTicketsData={resolveTicketsData}
        action={action}
      />
    ),

    [isOpen, resolveTicketsData, closeDialog, action]
  );

  return { dialog, openDialog };
};

type UseMergePatientsModalReturns = [
  ReactElement,
  (
    resolveTicketsData: ResolveTicketsData,
    duplicatePatientData: DuplicatePatientData[],
    action?: 'close' | 'resolve'
  ) => void
];

export function useMergePatientsModal(): UseMergePatientsModalReturns {
  const { isOpen, setIsOpen } = useToggle(false);
  const [resolveTicketsData, setResolveTicketsData] = useState<ResolveTicketsData>({
    tickets: [],
    comment: ''
  });
  const [duplicatePatientData, setDuplicatePatientsData] = useState<DuplicatePatientData[]>(null);
  const [action, setAction] = useState<'close' | 'resolve'>('resolve');

  const openModal = (
    resolveTicketsData: ResolveTicketsData,
    duplicatePatientData: DuplicatePatientData[],
    actionParam: 'close' | 'resolve' = 'resolve'
  ) => {
    setResolveTicketsData(resolveTicketsData);
    setDuplicatePatientsData(duplicatePatientData);
    setAction(actionParam);
    setIsOpen(true);
  };

  const modal = useMemo(
    () =>
      duplicatePatientData?.length ? (
        <MergeLocalPatientsModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          resolveTicketsData={resolveTicketsData}
          duplicatePatientData={duplicatePatientData}
          action={action}
        />
      ) : null,
    [isOpen, resolveTicketsData, setIsOpen, duplicatePatientData, action]
  );

  return [modal, openModal];
}
